import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Image, Stack } from "react-bootstrap";
import { User } from "../../types/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faBirthdayCake, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

interface HouseholdCardState {
  user: User;
  currentUser: User | undefined;
  setCurrentUser: any;
  userToSave: boolean;
  setShowModal: any;
  createMode?: boolean;
}

const HouseholdCard = ({ user, currentUser, setCurrentUser, userToSave, setShowModal, createMode = false }: HouseholdCardState) => {
  const [age, setAge] = useState<number | null>(15);
  const [profileImage, setProfileImage] = useState<string>("");
  const [isBirthday, setIsBirthday] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [showExplosion, setShowExplosion] = useState<boolean>(false);
  const [pieces, setPieces] = useState<Array<any>>([]);
  const triggeredRef = useRef<boolean>(false);
  const timeoutRef = useRef<number | null>(null);
  const [exploded, setExploded] = useState<boolean>(false);
  const [fadingInline, setFadingInline] = useState<boolean>(false);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setProfileImage("");
    user?.profile?.map((p: any) => {
      const base64String = "data:image/jpeg;base64, " + p?.data;
      setProfileImage(base64String);
    });

    // Check if birthdate is valid
    if (user?.birthdate && user?.birthdate !== "0000-00-00") {
      const calculateAge = (birthdate: string) => {
        const birthDateObj = new Date(birthdate);
        const today = new Date();

        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();

        // Adjust age if the birthdate hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
          age--;
        }
        return age;
      };

      setAge(calculateAge(user.birthdate));
    } else {
      setAge(null); // Don't calculate age if birthdate is invalid
    }

    // check if today is the user's birthday (month and day match)
    if (user.birthdate && user.birthdate !== "0000-00-00") {
      const parts = (user.birthdate || "").split("-").map((p: any) => parseInt(p, 10));
      if (parts.length >= 3 && !parts.some((n: number) => Number.isNaN(n))) {
        const [y, m, d] = parts;
        const bd = new Date(y, m - 1, d); // construct in local TZ to avoid UTC parse shifts
        const today = new Date();
        if (bd.getMonth() === today.getMonth() && bd.getDate() === today.getDate()) {
          setIsBirthday(true);
        } else {
          setIsBirthday(false);
        }
      } else {
        setIsBirthday(false);
      }
    } else {
      setIsBirthday(false);
    }
  }, [user]);

  // trigger explosion on demand (hover). Prevent rapid retriggers
  const triggerExplosion = () => {
    if (!isBirthday) return;
    if (triggeredRef.current) return;
    // start fading inline confetti and mark exploded after fade
    setFadingInline(true);
    if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = window.setTimeout(() => {
      setExploded(true);
      fadeTimeoutRef.current = null;
    }, 500);

    triggeredRef.current = true;

    const originX = Math.round(window.innerWidth / 2);
    const originY = Math.round(Math.min(120, window.innerHeight * 0.15));

    const count = 28;
    const newPieces = Array.from({ length: count }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 80 + Math.random() * 260; // pixels
      const tx = Math.round(Math.cos(angle) * dist) + "px";
      const ty = Math.round(Math.sin(angle) * dist) + "px";
      const delay = (Math.random() * 0.35).toFixed(2) + "s";
      const color = Math.floor(Math.random() * 6);
      return { x: originX + "px", y: originY + "px", tx, ty, delay, color };
    });

    setPieces(newPieces);
    setShowExplosion(true);

    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setShowExplosion(false);
      triggeredRef.current = false;
      timeoutRef.current = null;
    }, 1400);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  const handleOnClick = () => {
    if (userToSave) {
      setShowModal(true);
    } else {
      if (currentUser?.id === user?.id && currentUser?.relationship === user?.relationship) {
        setCurrentUser(currentUser);
      } else {
        setCurrentUser(undefined);
        setTimeout(() => {
          setCurrentUser(user);
        }, 0);
      }

      setShowModal(false);
    }
  };

  return (
    <>
      <Card
        ref={cardRef as any}
        className={`
          ${
            user?.deleted_at
              ? "bg-light card-household-condensed card-deleted "
              : user?.is_banned
                ? "bg-light card-household-condensed card-danger "
                : "bg-light card-household "
          } 
          ${currentUser?.id === user?.id && currentUser?.relationship === user?.relationship ? "card-household-active" : ""}`}
        onClick={createMode ? undefined : handleOnClick}
      >
        <Card.Body className="p-2">
          <Row className="me-0">
            <Col xs={3} className="ps-3 pe-0">
              {profileImage ? (
                <Image src={profileImage} rounded className="image-avatar-card w-100" />
              ) : (
                <Image src="/blank-profile-picture.jpg" rounded className="image-avatar-card w-100" />
              )}
            </Col>
            <Col xs={9}>
              <Stack>
                <h6
                  className={
                    currentUser?.id === user?.id && currentUser?.relationship === user?.relationship
                      ? "app-orange-text text-truncate pb-0 mb-0"
                      : "app-header-text text-truncates pb-0 mb-0"
                  }
                >
                  {user?.preferredname ? user.preferredname : user.firstname} {user.lastname}
                </h6>
                <p className="text-capitalize app-header-text text-truncate small mb-0">
                  {user.relationship === "child"
                    ? user?.grade && age
                      ? `Grade ${user?.grade} - Age ${age}`
                      : user?.grade
                        ? `Grade ${user?.grade}`
                        : age
                          ? `Age ${age}`
                          : ""
                    : user.relationship}
                </p>
              </Stack>
            </Col>
          </Row>
          {!user?.is_banned && !user?.deleted_at && (
            <Row>
              <Col xs={12}>
                <hr className="my-1" />
                {user?.program && (
                  <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                    {user?.program?.program_name} {user?.program?.level_name && " - " + user?.program?.level_name}
                  </p>
                )}
                {user?.bed && (
                  <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                    {user?.bed?.room_name + " - " + user?.bed?.name}
                  </p>
                )}
                {user?.job && (
                  <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                    {user?.job?.department_name + " - " + user?.job?.name}
                  </p>
                )}
                {user?.groups &&
                  (user?.groups?.length > 1 ? (
                    <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                      {user?.groups?.length + " Groups"}
                    </p>
                  ) : (
                    <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                      {user?.groups[0]?.name}
                    </p>
                  ))}
                {!user?.program && !user?.bed && !user?.job && user?.groups?.length === 0 && (
                  <p className="small mb-0 text-muted" style={{ fontSize: "0.7rem" }}>
                    No program and assignment
                  </p>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <Col xs={12} className="text-end">
              {user?.deleted_at ? (
                <div className="position-absolute top-0 end-0">
                  <Stack direction="horizontal">
                    <FontAwesomeIcon icon={faTrash} className="app-blue-text me-2 mt-2" />
                  </Stack>
                </div>
              ) : user?.is_banned === 1 ? (
                <div className="position-absolute top-0 end-0">
                  <Stack direction="horizontal">
                    <FontAwesomeIcon icon={faBan} className="app-danger-text me-2 mt-2" />
                  </Stack>
                </div>
              ) : (
                isBirthday &&
                user?.is_banned !== 1 &&
                exploded && (
                  <span
                    className="position-absolute top-0 end-0"
                    data-tooltip-id={`tooltip-bday-${user.id}`}
                    data-tooltip-content={`Birthday Today!`}
                    role="button"
                    tabIndex={0}
                    aria-label="Celebrate Birthday"
                    onMouseEnter={() => triggerExplosion()}
                    onFocus={() => triggerExplosion()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        triggerExplosion();
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <FontAwesomeIcon icon={faBirthdayCake} className="app-orange-text me-2 mt-2" />
                    <Tooltip id={`tooltip-bday-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                  </span>
                )
              )}
              {isBirthday && user?.is_banned !== 1 && !exploded && (
                <div
                  className={`position-absolute confetti-inline-container ${fadingInline ? "fade-confetti" : ""}`}
                  style={{ top: "1px", left: "48.5%", transform: "translateX(-50%)", width: "90%" }}
                  aria-hidden={showExplosion}
                  onMouseEnter={() => triggerExplosion()}
                  onFocus={() => triggerExplosion()}
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <span
                      key={i}
                      className={`confetti-inline-piece confetti-inline-${i % 6}`}
                      style={{ left: `${(i * (100 / (12 - 1))).toFixed(2)}%`, animationDelay: `${(i % 5) * 0.12}s` }}
                    />
                  ))}
                </div>
              )}
            </Col>
          </Row>
          <div className="position-absolute bottom-0 end-0 mb-2 me-2">
            <Stack direction="horizontal" gap={1}>
              {user?.is_case_subject === 1 && (
                <span
                  className="role-dot role-dot-green"
                  title="Person Served"
                  data-tooltip-id={`tooltip-person-${user.id}`}
                  data-tooltip-content={`Person Served`}
                >
                  <Tooltip id={`tooltip-person-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </span>
              )}
              {user?.is_residential === 1 && (
                <span
                  className="role-dot role-dot-blue"
                  title="Residential"
                  data-tooltip-id={`tooltip-person-${user.id}`}
                  data-tooltip-content={`Residential`}
                >
                  <Tooltip id={`tooltip-person-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </span>
              )}
              {user?.is_primary === 1 && (
                <span
                  className="role-dot role-dot-orange"
                  title="Main Contact"
                  data-tooltip-id={`tooltip-contact-${user.id}`}
                  data-tooltip-content={`Main Contact`}
                >
                  <Tooltip id={`tooltip-contact-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </span>
              )}
            </Stack>
          </div>
        </Card.Body>
      </Card>
      {showExplosion && (
        <div className="confetti-explosion" aria-hidden>
          {pieces.map((p: any, i: number) => (
            <span
              key={i}
              className={`confetti-piece confetti-${p.color}`}
              style={{
                ["--x" as any]: p.x,
                ["--y" as any]: p.y,
                ["--tx" as any]: p.tx,
                ["--ty" as any]: p.ty,
                animationDelay: p.delay,
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default HouseholdCard;
